import { Column } from "primereact/column";
import { useMemo, useState } from "react";
import { useToastContext } from "../../../../contexts/ToastContext/ToastContext";
import localeText from "../../../../locale";
import CatalogTable from "../../components/list/CatalogTable";
import ConfirmActionDialog from "../../components/list/ConfirmActionDialog";
import StatusBadge from "../../components/list/StatusBadge";
import useAdminList from "../../hooks/useAdminList";
import { buildSearchText, redirectIfUnauthorized } from "../../utils/adminUtils";
import { percentText } from "../../../Channel/channelUtils";
import { callChannel, channelService, formatDate } from "./adminScontiPosteUtils";

const sellerStatus = (row) => {
  if (!row.active) return { tone: "neutral", label: localeText("adminChannelStatusLeft") };
  if (row.termsOutdated) return { tone: "warning", label: localeText("adminChannelStatusOutdated") };
  return { tone: "good", label: localeText("adminChannelStatusActive") };
};

const toRow = (seller) => ({
  ...seller,
  joinedValue: seller.termsAcceptedDate ? new Date(seller.termsAcceptedDate).getTime() : 0,
  searchText: buildSearchText([seller.username, seller.name, seller.email]),
});

// Managers que aderiram ao canal: % aceito, produtos na vitrine e suspender/reativar
const ChannelSellersSection = () => {
  const { showErrorToast } = useToastContext();
  const { items, setItems, loading, loadError, reload } = useAdminList(async () =>
    (await callChannel(() => channelService.adminSellers())) || [],
  );
  const [target, setTarget] = useState(null);
  const [saving, setSaving] = useState(false);

  const rows = useMemo(() => items.map(toRow), [items]);

  const setActive = async (row, active) => {
    setSaving(true);
    try {
      setItems((await callChannel(() => channelService.adminSetSellerActive(row.id, active))) || []);
      setTarget(null);
    } catch (e) {
      if (!redirectIfUnauthorized(e)) showErrorToast({ detail: e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <CatalogTable
        rows={rows}
        loading={loading}
        loadError={loadError}
        onReload={reload}
        searchPlaceholder={localeText("adminChannelSearchSellers")}
        sortField="joinedValue"
        sortOrder={-1}
        minWidth="56rem"
      >
        <Column
          header={localeText("adminChannelSeller")}
          field="name"
          sortable
          body={(row) => (
            <div className="admin-stack">
              <span className="admin-strong">{row.name}</span>
              <span className="admin-muted">@{row.username}</span>
              {row.email && <span className="admin-muted">{row.email}</span>}
            </div>
          )}
        />
        <Column
          header={localeText("adminChannelJoined")}
          field="joinedValue"
          sortable
          body={(row) => formatDate(row.termsAcceptedDate)}
        />
        <Column
          header={localeText("adminChannelTermsPercent")}
          field="termsCashbackPercent"
          sortable
          body={(row) => <span className="admin-num">{percentText(row.termsCashbackPercent)}%</span>}
        />
        <Column
          header={localeText("adminChannelProductsMode")}
          field="allProducts"
          body={(row) =>
            row.allProducts ? localeText("adminChannelAllProducts") : localeText("adminChannelSelectedProducts")
          }
        />
        <Column
          header={localeText("adminChannelListed")}
          field="listedProducts"
          sortable
          body={(row) => <span className="admin-num">{row.listedProducts}</span>}
        />
        <Column
          header={localeText("adminChannelSellerStatus")}
          body={(row) => {
            const status = sellerStatus(row);
            return <StatusBadge tone={status.tone}>{status.label}</StatusBadge>;
          }}
        />
        <Column
          body={(row) =>
            row.active ? (
              <button type="button" className="admin-btn admin-btn--danger-hover" onClick={() => setTarget(row)}>
                {localeText("adminChannelSuspend")}
              </button>
            ) : (
              <button type="button" className="admin-btn" onClick={() => setActive(row, true)} disabled={saving}>
                {localeText("adminChannelReactivate")}
              </button>
            )
          }
        />
      </CatalogTable>

      <ConfirmActionDialog
        visible={!!target}
        title={localeText("adminChannelSuspendTitle")}
        message={target ? localeText("adminChannelSuspendConfirm", { name: target.name }) : ""}
        confirmLabel={localeText("adminChannelSuspend")}
        confirmTone="danger"
        loading={saving}
        onConfirm={() => setActive(target, false)}
        onCancel={() => setTarget(null)}
      />
    </>
  );
};

export default ChannelSellersSection;
